import React, { createContext, useContext, useState } from 'react';

const translations = {
  fr: {
    // Navigation
    nav: {
      services: 'Services',
      zone: "Zone d'activité",
      contact: 'Contact',
      cta: 'Réserver',
    },
    // Hero
    hero: {
      headline: 'Votre chauffeur privé dans le canton de Vaud',
      subheadline: 'Service de chauffeur privé sur réservation pour vos déplacements personnels et professionnels dans le canton de Vaud.',
      cta: 'Réserver',
      ctaSecondary: 'Découvrir le service',
      badge: 'Véhicule électrique',
    },
    // Services
    services: {
      title: 'Nos services',
      airport: {
        title: 'Transferts aéroport',
        description: 'Un service ponctuel et soigné pour vos transferts sur réservation.',
      },
      private: {
        title: 'Déplacements privés',
        description: 'Des trajets pensés pour le confort, la discrétion et la sérénité.',
      },
      business: {
        title: 'Transport professionnel',
        description: "Une solution adaptée aux rendez-vous, déplacements d'affaires et transferts clients.",
      },
    },
    // Zone
    zone: {
      title: 'Un service présent dans le canton de Vaud',
      description: 'Nous assurons vos déplacements dans tout le canton de Vaud et vers les destinations clés de la région.',
      cities: ['Lausanne', 'Vevey', 'Montreux', 'Nyon', 'Morges', 'Yverdon-les-Bains'],
      airport: 'Transferts vers Genève Aéroport',
    },
    // Why Choose
    why: {
      title: 'Pourquoi choisir HB Transfert',
      discretion: {
        title: 'Discrétion',
        description: 'Un service confidentiel et respectueux de votre vie privée.',
      },
      punctuality: {
        title: 'Ponctualité',
        description: 'Une rigueur absolue dans le respect des horaires convenus.',
      },
      comfort: {
        title: 'Confort',
        description: 'Des véhicules premium pour un trajet en toute sérénité.',
      },
      reservation: {
        title: 'Service sur réservation',
        description: 'Un accompagnement personnalisé selon vos besoins.',
      },
      languages: 'Communication en français et anglais',
    },
    // Contact
    contact: {
      title: 'Contactez-nous',
      description: 'Pour toute demande de réservation ou information, nous sommes à votre écoute.',
      call: 'Appeler',
      email: 'Envoyer un email',
      whatsapp: 'WhatsApp',
      form: {
        title: 'Demander une réservation',
        name: 'Nom',
        emailField: 'Email',
        phone: 'Téléphone',
        message: 'Votre message',
        submit: 'Envoyer la demande',
      },
    },
    // Footer
    footer: {
      baseline: 'HB Transfert — Chauffeur privé sur réservation dans le canton de Vaud.',
      legal: 'Mentions légales',
      privacy: 'Politique de confidentialité',
      copyright: '© 2026 HB Transfert - Tous droits réservés',
    },
    // Legal pages
    legal: {
      title: 'Mentions légales',
      backToHome: 'Retour à l\'accueil',
      sections: {
        editor: {
          title: 'Éditeur du site',
          content: 'HB Transfert\nService de chauffeur privé\nCanton de Vaud, Suisse',
        },
        contact: {
          title: 'Contact',
          content: 'Email : contact@hbtransfert.com\nTéléphone : +33 6 19 36 25 20',
        },
        hosting: {
          title: 'Hébergement',
          content: 'Ce site est hébergé par des services cloud professionnels.',
        },
        ip: {
          title: 'Propriété intellectuelle',
          content: "L'ensemble du contenu de ce site (textes, images, graphismes, logo) est la propriété exclusive de HB Transfert, sauf mention contraire. Toute reproduction, distribution, modification ou utilisation de ces contenus est interdite sans autorisation préalable.",
        },
        liability: {
          title: 'Responsabilité',
          content: "HB Transfert s'efforce d'assurer l'exactitude des informations diffusées sur ce site. Toutefois, HB Transfert ne peut garantir l'exactitude, la précision ou l'exhaustivité des informations mises à disposition. En conséquence, HB Transfert décline toute responsabilité pour les éventuelles imprécisions, inexactitudes ou omissions portant sur des informations disponibles sur le site.",
        },
      },
    },
    privacy: {
      title: 'Politique de confidentialité',
      backToHome: 'Retour à l\'accueil',
      sections: {
        intro: {
          title: 'Introduction',
          content: "HB Transfert s'engage à protéger la vie privée des utilisateurs de son site. Cette politique de confidentialité explique comment nous collectons, utilisons et protégeons vos informations personnelles.",
        },
        collect: {
          title: 'Données collectées',
          content: "Lorsque vous utilisez notre formulaire de contact ou nos services, nous pouvons collecter : votre nom, votre adresse email, votre numéro de téléphone, ainsi que toute information que vous choisissez de nous communiquer dans vos messages.",
        },
        use: {
          title: 'Utilisation des données',
          content: "Vos données personnelles sont utilisées uniquement pour répondre à vos demandes, gérer vos réservations et améliorer nos services. Nous ne vendons ni ne partageons vos données avec des tiers à des fins commerciales.",
        },
        security: {
          title: 'Sécurité',
          content: "Nous mettons en œuvre des mesures de sécurité appropriées pour protéger vos données personnelles contre tout accès non autorisé, modification, divulgation ou destruction.",
        },
        rights: {
          title: 'Vos droits',
          content: "Conformément à la législation applicable, vous disposez d'un droit d'accès, de rectification et de suppression de vos données personnelles. Pour exercer ces droits, veuillez nous contacter à l'adresse : contact@hbtransfert.com",
        },
        contact: {
          title: 'Contact',
          content: "Pour toute question concernant cette politique de confidentialité, veuillez nous contacter à : contact@hbtransfert.com",
        },
      },
    },
  },
  en: {
    // Navigation
    nav: {
      services: 'Services',
      zone: 'Coverage Area',
      contact: 'Contact',
      cta: 'Book Now',
    },
    // Hero
    hero: {
      headline: 'Your private driver in the Canton of Vaud',
      subheadline: 'Private driver service by reservation for your personal and professional trips in the Canton of Vaud.',
      cta: 'Book Now',
      ctaSecondary: 'Discover our service',
      badge: 'Electric vehicle',
    },
    // Services
    services: {
      title: 'Our Services',
      airport: {
        title: 'Airport Transfers',
        description: 'A punctual and refined service for your transfers by reservation.',
      },
      private: {
        title: 'Private Trips',
        description: 'Journeys designed for comfort, discretion and peace of mind.',
      },
      business: {
        title: 'Business Transport',
        description: 'A solution tailored to meetings, business travel and client transfers.',
      },
    },
    // Zone
    zone: {
      title: 'A service available throughout the Canton of Vaud',
      description: 'We provide transportation throughout the Canton of Vaud and to key destinations in the region.',
      cities: ['Lausanne', 'Vevey', 'Montreux', 'Nyon', 'Morges', 'Yverdon-les-Bains'],
      airport: 'Geneva Airport transfer service',
    },
    // Why Choose
    why: {
      title: 'Why Choose HB Transfert',
      discretion: {
        title: 'Discretion',
        description: 'A confidential service that respects your privacy.',
      },
      punctuality: {
        title: 'Punctuality',
        description: 'Absolute rigor in respecting agreed schedules.',
      },
      comfort: {
        title: 'Comfort',
        description: 'Premium vehicles for a serene journey.',
      },
      reservation: {
        title: 'Reservation Service',
        description: 'Personalized support tailored to your needs.',
      },
      languages: 'Communication in French and English',
    },
    // Contact
    contact: {
      title: 'Contact Us',
      description: 'For any reservation request or information, we are at your service.',
      call: 'Call',
      email: 'Send an email',
      whatsapp: 'WhatsApp',
      form: {
        title: 'Request a Reservation',
        name: 'Name',
        emailField: 'Email',
        phone: 'Phone',
        message: 'Your message',
        submit: 'Send request',
      },
    },
    // Footer
    footer: {
      baseline: 'HB Transfert — Private driver service by reservation in the Canton of Vaud.',
      legal: 'Legal Notice',
      privacy: 'Privacy Policy',
      copyright: '© 2026 HB Transfert - All rights reserved',
    },
    // Legal pages
    legal: {
      title: 'Legal Notice',
      backToHome: 'Back to Home',
      sections: {
        editor: {
          title: 'Website Publisher',
          content: 'HB Transfert\nPrivate Chauffeur Service\nCanton de Vaud, Switzerland',
        },
        contact: {
          title: 'Contact',
          content: 'Email: contact@hbtransfert.com\nPhone: +33 6 19 36 25 20',
        },
        hosting: {
          title: 'Hosting',
          content: 'This website is hosted by professional cloud services.',
        },
        ip: {
          title: 'Intellectual Property',
          content: 'All content on this website (texts, images, graphics, logo) is the exclusive property of HB Transfert, unless otherwise stated. Any reproduction, distribution, modification or use of this content is prohibited without prior authorization.',
        },
        liability: {
          title: 'Liability',
          content: 'HB Transfert strives to ensure the accuracy of information published on this website. However, HB Transfert cannot guarantee the accuracy, precision or completeness of information made available. Consequently, HB Transfert declines all responsibility for any inaccuracies, errors or omissions in the information available on the site.',
        },
      },
    },
    privacy: {
      title: 'Privacy Policy',
      backToHome: 'Back to Home',
      sections: {
        intro: {
          title: 'Introduction',
          content: 'HB Transfert is committed to protecting the privacy of users of its website. This privacy policy explains how we collect, use and protect your personal information.',
        },
        collect: {
          title: 'Data Collected',
          content: 'When you use our contact form or services, we may collect: your name, email address, phone number, and any information you choose to share with us in your messages.',
        },
        use: {
          title: 'Use of Data',
          content: 'Your personal data is used only to respond to your requests, manage your reservations and improve our services. We do not sell or share your data with third parties for commercial purposes.',
        },
        security: {
          title: 'Security',
          content: 'We implement appropriate security measures to protect your personal data against unauthorized access, modification, disclosure or destruction.',
        },
        rights: {
          title: 'Your Rights',
          content: 'In accordance with applicable legislation, you have the right to access, rectify and delete your personal data. To exercise these rights, please contact us at: contact@hbtransfert.com',
        },
        contact: {
          title: 'Contact',
          content: 'For any questions regarding this privacy policy, please contact us at: contact@hbtransfert.com',
        },
      },
    },
  },
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('fr');

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  const toggleLanguage = (lang) => {
    setLanguage(lang);
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage: toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;